import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { ITEMS, type ItemId } from "../data/items";
import { CHAMPIONS, TIER_COLORS } from "../data/champions";
import { championIconUrl, itemIconUrl } from "../config/constants";
import type { MapNode } from "../map/CampaignMap";
import { MapScene } from "./MapScene";
import { levelForXp } from "../units/UnitStats";

export class ItemPickScene implements Scene {
  private root: HTMLDivElement;

  constructor(
    private game: Game,
    private node: MapNode,
    private offers: ItemId[],
  ) {
    this.root = document.createElement("div");
    this.root.className = "choice-screen";
  }

  enter(): void {
    this.renderPickStep();
    this.game.sceneRoot.appendChild(this.root);
  }

  exit(): void {
    this.root.remove();
  }

  update(_dt: number): void {}
  render(_ctx: CanvasRenderingContext2D): void {}

  private renderPickStep(): void {
    this.root.innerHTML = `
      <div class="reward-title">ITEM REWARD</div>
      <div style="color:var(--text-dim);font-size:11px;letter-spacing:2px;margin-bottom:8px">CHOOSE AN ITEM</div>
      <button class="btn ghost btn-sm" id="skip-btn">SKIP</button>
      <div class="choice-card-list" id="item-offers"></div>
    `;
    this.root.style.paddingBottom = "calc(var(--ad-anchor-height) + 80px)";
    const list = this.root.querySelector("#item-offers") as HTMLDivElement;

    for (const id of this.offers) {
      const def = ITEMS[id];
      const iconSrc = def.ddragonItemId ? itemIconUrl(def.ddragonItemId) : "";
      const card = document.createElement("div");
      card.className = "choice-card";
      card.innerHTML = `
        <div class="icon" style="${iconSrc ? `background-image:url('${iconSrc}')` : ""}"></div>
        <div class="info">
          <div class="title">${def.name}</div>
          <div class="desc">${def.description}</div>
        </div>
      `;
      card.addEventListener("click", () => this.renderAssignStep(id));
      list.appendChild(card);
    }

    (this.root.querySelector("#skip-btn") as HTMLButtonElement)
      .addEventListener("click", () => this.finish());

    this.appendTeamBar();
  }

  private renderAssignStep(itemId: ItemId): void {
    const state = this.game.runState;
    if (!state) return;
    const def = ITEMS[itemId];
    const iconSrc = def.ddragonItemId ? itemIconUrl(def.ddragonItemId) : "";

    this.root.innerHTML = `
      <div class="reward-title">ASSIGN ITEM</div>
      <div style="color:var(--text-dim);font-size:11px;letter-spacing:1px;margin-bottom:10px;display:flex;align-items:center;gap:8px;justify-content:center">
        ${iconSrc ? `<img src="${iconSrc}" style="width:24px;height:24px;border-radius:3px;border:1px solid #2a3446" />` : ""}
        <span style="color:var(--accent-hot)">${def.name}</span> → who gets it?
      </div>
      <div class="choice-card-list" id="assign-cards"></div>
      <button class="btn ghost btn-sm" id="cancel-btn">CANCEL</button>
    `;

    const list = this.root.querySelector("#assign-cards") as HTMLDivElement;

    for (const entry of state.roster) {
      const champDef = CHAMPIONS[entry.championId];
      const tierColor = TIER_COLORS[champDef.tier];
      const level = levelForXp(entry.xp);
      const existingItem = entry.itemId ? ITEMS[entry.itemId] : null;

      const card = document.createElement("div");
      card.className = "choice-card";
      card.style.borderColor = tierColor;
      const existingIconHtml = existingItem?.ddragonItemId
        ? `<img src="${itemIconUrl(existingItem.ddragonItemId)}" style="width:18px;height:18px;border-radius:2px;vertical-align:middle;margin-right:3px" />`
        : "";
      card.innerHTML = `
        <div class="icon" style="background-image:url('${championIconUrl(champDef.iconId)}');border-radius:50%;border-color:${tierColor}"></div>
        <div class="info">
          <div class="title">${champDef.displayName} <span style="color:var(--text-dim);font-size:10px">Lv.${level}</span></div>
          <div class="desc">${existingItem ? `${existingIconHtml}Replaces: ${existingItem.name}` : "No item equipped"}</div>
        </div>
      `;
      const entryId = entry.id;
      card.addEventListener("click", () => {
        const r = state.roster.find((x) => x.id === entryId);
        if (r) r.itemId = itemId;
        this.finish();
      });
      list.appendChild(card);
    }

    (this.root.querySelector("#cancel-btn") as HTMLButtonElement)
      .addEventListener("click", () => this.renderPickStep());
  }

  private appendTeamBar(): void {
    const state = this.game.runState;
    if (!state) return;
    const teamBar = document.createElement("div");
    teamBar.className = "recruit-team-bar";
    const label = document.createElement("div");
    label.className = "recruit-team-label";
    label.textContent = "YOUR TEAM";
    teamBar.appendChild(label);
    const icons = document.createElement("div");
    icons.className = "recruit-team-icons";
    for (const entry of state.roster) {
      const def = CHAMPIONS[entry.championId];
      const level = levelForXp(entry.xp);
      const itemName = entry.itemId ? ITEMS[entry.itemId].name : "";
      const slot = document.createElement("div");
      slot.className = "recruit-team-slot";
      slot.title = `${def.displayName}${itemName ? ` · ${itemName}` : ""}`;
      slot.innerHTML = `
        <div class="recruit-team-icon" style="background-image:url('${championIconUrl(def.iconId)}')"></div>
        <div class="recruit-team-level">Lv.${level}</div>
      `;
      icons.appendChild(slot);
    }
    teamBar.appendChild(icons);
    this.root.appendChild(teamBar);
  }

  private finish(): void {
    const state = this.game.runState;
    if (!state) return;
    state.pendingItemOffers = null;
    this.game.achievements.onRosterUpdate(state.roster);
    state.map?.markComplete(this.node.id);
    this.game.sceneManager.transition(new MapScene(this.game));
  }
}
